import React, { useState, useEffect } from 'react';
import { Clock, LogOut, Globe, Loader, AlertCircle, ChevronLeft, ChevronRight, Trash2, CheckCircle } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const itemsPerPage = 15;

  const isSuperAdmin = currentUserRole === 'superadmin';

  useMountEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      setError('Please login to access this page');
      setLoading(false);
      return;
    }

    const user = JSON.parse(userData);
    setCurrentUserRole(user.role);
    fetchSessions(1);
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSessions(currentPage);
    }
  }, [currentPage, sortOrder]);

  const fetchSessions = async (page = 1) => {
    try {
      setLoading(true);
      const url = `/api/sessions?page=${page}&limit=${itemsPerPage}&sort=${sortOrder}`;
      const data = await apiCall(url);

      // Handle paginated response format
      if (data.data && data.pagination) {
        setSessions(data.data);
        setTotalSessions(data.pagination.total);
        setCurrentPage(data.pagination.page);
      } else {
        // Fallback for old format
        setSessions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSessions = async (days) => {
    try {
      setClearing(true);
      const data = await apiCall('/api/sessions/clear-old', {
        method: 'POST',
        body: JSON.stringify({ days })
      });

      if (data && data.message) {
        setSuccess(`✓ ${data.message}`);
        setConfirmClear(null);
        fetchSessions(1);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(data?.error || 'Failed to clear sessions');
      }
    } catch (err) {
      console.error('Error clearing sessions:', err);
      setError('Failed to clear sessions');
    } finally {
      setClearing(false);
    }
  };

  const handleLogoutSession = async (id) => {
    try {
      const response = await fetch(`/api/sessions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setSessions(sessions.filter(s => s.id !== id));
        setSuccess('✓ Session ended successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to logout session');
      }
    } catch (err) {
      console.error('Error logging out session:', err);
      setError('Error logging out session');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><Clock size={28} /> Active Sessions</h1>
          <p>Monitor and manage user sessions</p>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spinner" style={{ margin: '0 auto', color: '#3b82f6' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading sessions...</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalSessions / itemsPerPage);

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Clock size={28} /> Active Sessions</h1>
          <p>Monitor and manage user sessions</p>
        </div>
        {!isSuperAdmin && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: '#f3f4f6',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            🔒 Read Only
          </div>
        )}
      </div>

      {error && (
        <div className="status-message error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3>Sessions ({totalSessions})</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>Sort:</label>
              <button
                className={`filter-btn ${sortOrder === 'asc' ? 'active' : ''}`}
                onClick={() => setSortOrder('asc')}
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                ↑ Ascending
              </button>
              <button
                className={`filter-btn ${sortOrder === 'desc' ? 'active' : ''}`}
                onClick={() => setSortOrder('desc')}
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                ↓ Descending
              </button>
            </div>
            <button
              className="section-action-btn"
              onClick={() => fetchSessions(currentPage)}
              style={{ background: '#3b82f6', color: 'white', border: 'none' }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {isSuperAdmin && (
          <div style={{ marginBottom: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Clear Sessions:</span>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 1, label: 'yesterday' })}
              disabled={clearing}
              title="Clear sessions from yesterday"
            >
              <Trash2 size={12} />
              <span>Yesterday</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 7, label: 'last 7 days' })}
              disabled={clearing}
              title="Clear sessions from last 7 days"
            >
              <Trash2 size={12} />
              <span>7 Days</span>
            </button>
            <button
              className="action-btn clear-btn"
              onClick={() => setConfirmClear({ days: 30, label: 'last 30 days' })}
              disabled={clearing}
              title="Clear sessions from last 30 days"
            >
              <Trash2 size={12} />
              <span>Month</span>
            </button>
          </div>
        )}

        {sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Clock size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No active sessions</p>
            <p style={{ fontSize: '14px' }}>Sessions are created when users log in</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '16px' }}>
              {sessions.map(session => (
                <div key={session.id} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'start', flex: 1 }}>
                      <Globe size={20} style={{ color: '#3b82f6', marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                          {session.user || 'Unknown User'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>
                          {session.device || 'Unknown Device'}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280', flexWrap: 'wrap' }}>
                          {session.os && <span>💻 {session.os}</span>}
                          {session.browser && <span>🌐 {session.browser}</span>}
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn btn-sm"
                      onClick={() => handleLogoutSession(session.id)}
                      title="End this session"
                      style={{ background: '#fee2e2', color: '#991b1b', border: 'none' }}
                      disabled={!isSuperAdmin}
                    >
                      <LogOut size={14} />
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '12px', color: '#64748b' }}>
                    <div>
                      <span style={{ display: 'block', fontWeight: '500', color: '#1e293b', marginBottom: '2px' }}>IP Address</span>
                      {session.ip || 'N/A'}
                    </div>
                    <div>
                      <span style={{ display: 'block', fontWeight: '500', color: '#1e293b', marginBottom: '2px' }}>Logged In</span>
                      {session.loggedIn || 'N/A'}
                    </div>
                    <div>
                      <span style={{ display: 'block', fontWeight: '500', color: '#1e293b', marginBottom: '2px' }}>Last Active</span>
                      {session.lastActive || 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                Showing {sessions.length} of {totalSessions} sessions (Page {currentPage} of {totalPages})
              </span>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={14} />
                </button>

                <span style={{ fontSize: '12px', color: '#1e293b', fontWeight: '600', minWidth: '60px', textAlign: 'center' }}>
                  Page {currentPage}
                </span>

                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmClear && (
        <div className="modal-overlay" onClick={() => setConfirmClear(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Clear Sessions</h3>
            <p>
              This will permanently delete all sessions from the {confirmClear.label}. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmClear(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleClearSessions(confirmClear.days)}
                disabled={clearing}
              >
                {clearing ? (
                  <>
                    <Loader size={14} className="spinner" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Delete Sessions
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions;
